import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import TopSnack from "components/TopSnack";
import useEinvoicing from "services/hooks/useEinvoicing";

const EMPTY_CONFIG = {
  environment: "sandbox",
  tin: "",
  device_no: "",
  branch_id: "",
  business_name: "",
  private_key_pem: "",
  private_key_passphrase: "",
  has_private_key: false,
  goods_code: "RENT-001",
  goods_name: "Rent",
  goods_category_id: "",
  measure_unit: "102",
  tax_rate: "0",
  currency: "101",
  document_kind: "receipt",
};

const EMPTY_INVOICE = {
  payment_id: "",
  amount: "",
  buyer_name: "",
  description: "",
};

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

const EInvoicing = () => {
  const einvoicingEnabled = useSelector((state) =>
    Boolean(state.einvoicingEnabled || state.landlordDetails?.einvoicing_enabled)
  );
  const {
    getConfig,
    updateConfig,
    testConnection,
    refreshAesKey,
    syncGoods,
    listInvoices,
    issueInvoice,
  } = useEinvoicing();

  const [tab, setTab] = useState(0);
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [invoiceForm, setInvoiceForm] = useState(EMPTY_INVOICE);
  const [invoices, setInvoices] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [issuingInvoice, setIssuingInvoice] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [error, setError] = useState("");
  const [openSnack, setOpenSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnack = (message, severity = "success") => {
    setOpenSnack({ open: true, message, severity });
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnack((prev) => ({ ...prev, open: false }));
  };

  const handleConfigChange = (event) => {
    const { name, value } = event.target;
    setConfig((prev) => ({ ...prev, [name]: value }));
  };

  const handleInvoiceChange = (event) => {
    const { name, value } = event.target;
    setInvoiceForm((prev) => ({ ...prev, [name]: value }));
  };

  const loadConfig = async () => {
    setLoadingConfig(true);
    setError("");
    try {
      const response = await getConfig();
      const data = response?.data || response || {};
      setConfig({
        ...EMPTY_CONFIG,
        ...data,
        // Never put masked/redacted key material into the form — leave blank to keep existing.
        private_key_pem: "",
        private_key_passphrase: "",
      });
    } catch (err) {
      setError(err?.response?.data?.error || "Unable to load e-invoicing config");
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const response = await listInvoices();
      const rows = response?.data || response || [];
      setInvoices(Array.isArray(rows) ? rows : rows?.results || []);
    } catch (err) {
      showSnack(err?.response?.data?.error || "Unable to load invoices", "error");
    } finally {
      setLoadingInvoices(false);
    }
  };

  useEffect(() => {
    if (!einvoicingEnabled) return;
    loadConfig();
  }, [einvoicingEnabled]);

  useEffect(() => {
    if (!einvoicingEnabled || tab !== 2) return;
    loadInvoices();
  }, [einvoicingEnabled, tab]);

  if (!einvoicingEnabled) {
    return <Navigate to="/home" replace />;
  }

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    setError("");
    try {
      const payload = { ...config };
      if (!payload.private_key_pem?.trim()) {
        delete payload.private_key_pem;
      }
      if (!payload.private_key_passphrase?.trim()) {
        delete payload.private_key_passphrase;
      }
      const response = await updateConfig(payload);
      showSnack(response?.data?.message || "E-invoicing config saved");
      await loadConfig();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save config");
      showSnack(err?.response?.data?.error || "Failed to save config", "error");
    } finally {
      setSavingConfig(false);
    }
  };

  const runAction = async (actionKey, actionFn, successMessage) => {
    setActionLoading(actionKey);
    try {
      const response = await actionFn();
      showSnack(response?.data?.message || response?.message || successMessage);
    } catch (err) {
      showSnack(err?.response?.data?.error || `Failed: ${successMessage}`, "error");
    } finally {
      setActionLoading("");
    }
  };

  const handleIssueInvoice = async () => {
    setIssuingInvoice(true);
    try {
      const payload = invoiceForm.payment_id
        ? { payment_id: Number(invoiceForm.payment_id) }
        : {
            amount: invoiceForm.amount,
            buyer_name: invoiceForm.buyer_name,
            description: invoiceForm.description,
          };

      if (!payload.payment_id && (!payload.amount || !payload.buyer_name)) {
        showSnack("Provide a payment ID, or amount and buyer name", "error");
        return;
      }

      const response = await issueInvoice(payload);
      showSnack(response?.data?.message || "Fiscal invoice issued");
      setInvoiceForm(EMPTY_INVOICE);
      await loadInvoices();
      setTab(2);
    } catch (err) {
      showSnack(err?.response?.data?.error || "Failed to issue invoice", "error");
    } finally {
      setIssuingInvoice(false);
    }
  };

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight={700} mb={1}>
        E-Invoicing (URA EFRIS)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Configure your taxpayer credentials, test the EFRIS connection, and issue fiscal invoices.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tab} onChange={(_, next) => setTab(next)} variant="scrollable" allowScrollButtonsMobile>
          <Tab label="Configuration" />
          <Tab label="Connection & Goods" />
          <Tab label="Invoices" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        {loadingConfig ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={600}>
                URA / EFRIS credentials
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Environment</InputLabel>
                <Select
                  label="Environment"
                  name="environment"
                  value={config.environment || "sandbox"}
                  onChange={handleConfigChange}
                >
                  <MenuItem value="sandbox">Sandbox</MenuItem>
                  <MenuItem value="production">Production</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                label="TIN"
                name="tin"
                value={config.tin}
                onChange={handleConfigChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                label="Device number"
                name="device_no"
                value={config.device_no}
                onChange={handleConfigChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                label="Branch ID (optional)"
                name="branch_id"
                value={config.branch_id}
                onChange={handleConfigChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="dense"
                label="Business name"
                name="business_name"
                value={config.business_name}
                onChange={handleConfigChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="dense"
                multiline
                minRows={5}
                label="Private key (PEM)"
                name="private_key_pem"
                value={config.private_key_pem}
                onChange={handleConfigChange}
                placeholder="-----BEGIN PRIVATE KEY-----"
                helperText={
                  config.has_private_key
                    ? "A private key is already saved. Leave blank to keep it, or paste a new PEM to replace it."
                    : "Paste the RSA private key that matches the .cer uploaded to URA."
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                type="password"
                label="Private key passphrase (optional)"
                name="private_key_passphrase"
                value={config.private_key_passphrase}
                onChange={handleConfigChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" fontWeight={600} mt={1}>
                Default goods / rent service
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                label="Goods code"
                name="goods_code"
                value={config.goods_code}
                onChange={handleConfigChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                label="Goods name"
                name="goods_name"
                value={config.goods_name}
                onChange={handleConfigChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                label="Goods category ID"
                name="goods_category_id"
                value={config.goods_category_id}
                onChange={handleConfigChange}
                helperText="EFRIS commodity classification ID from the portal Goods/Services list (e.g. 80131501). Do NOT enter T130 — that is an API interface code."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                label="Measure unit"
                name="measure_unit"
                value={config.measure_unit}
                onChange={handleConfigChange}
                helperText="EFRIS code for this category: 102 = Per month (matches portal)."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                label="Tax rate"
                name="tax_rate"
                value={config.tax_rate}
                onChange={handleConfigChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                label="Currency"
                name="currency"
                value={config.currency}
                onChange={handleConfigChange}
                helperText="EFRIS code: 101 = UGX (not the letters UGX)."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="document-kind-label">Document kind</InputLabel>
                <Select
                  labelId="document-kind-label"
                  label="Document kind"
                  name="document_kind"
                  value={config.document_kind || "receipt"}
                  onChange={handleConfigChange}
                >
                  <MenuItem value="receipt">E-receipt (non-VAT)</MenuItem>
                  <MenuItem value="invoice">E-invoice (VAT-registered)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleSaveConfig}
                disabled={savingConfig}
                sx={{
                  mt: 1,
                  backgroundColor: "#FCB200",
                  color: "#fff",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "#e0a000" },
                }}
              >
                {savingConfig ? "Saving..." : "Save configuration"}
              </Button>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Typography variant="body1" mb={2}>
          After saving credentials, test the connection, refresh the daily AES key, then sync your default rent goods to EFRIS.
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <Button
            variant="outlined"
            disabled={Boolean(actionLoading)}
            onClick={() =>
              runAction("test", testConnection, "Connection test succeeded")
            }
          >
            {actionLoading === "test" ? "Testing..." : "Test connection"}
          </Button>
          <Button
            variant="outlined"
            disabled={Boolean(actionLoading)}
            onClick={() =>
              runAction("aes", refreshAesKey, "AES key refreshed")
            }
          >
            {actionLoading === "aes" ? "Refreshing..." : "Refresh AES key"}
          </Button>
          <Button
            variant="outlined"
            disabled={Boolean(actionLoading)}
            onClick={() => runAction("goods", syncGoods, "Goods synced")}
          >
            {actionLoading === "goods" ? "Syncing..." : "Sync goods"}
          </Button>
        </Box>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Issue fiscal invoice
        </Typography>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="dense"
              label="Payment ID (optional)"
              name="payment_id"
              value={invoiceForm.payment_id}
              onChange={handleInvoiceChange}
              helperText="If set, invoice is issued from this payment"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="dense"
              label="Amount"
              name="amount"
              value={invoiceForm.amount}
              onChange={handleInvoiceChange}
              disabled={Boolean(invoiceForm.payment_id)}
              helperText="Used for manual invoices"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="dense"
              label="Buyer name"
              name="buyer_name"
              value={invoiceForm.buyer_name}
              onChange={handleInvoiceChange}
              disabled={Boolean(invoiceForm.payment_id)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="dense"
              label="Description"
              name="description"
              value={invoiceForm.description}
              onChange={handleInvoiceChange}
              disabled={Boolean(invoiceForm.payment_id)}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleIssueInvoice}
              disabled={issuingInvoice}
              sx={{
                backgroundColor: "#FCB200",
                color: "#fff",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#e0a000" },
              }}
            >
              {issuingInvoice ? "Issuing..." : "Issue fiscal receipt"}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Issued invoices
          </Typography>
          <Button size="small" onClick={loadInvoices} disabled={loadingInvoices}>
            Refresh
          </Button>
        </Box>

        {loadingInvoices ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : invoices.length === 0 ? (
          <Typography color="text.secondary">No fiscal invoices yet.</Typography>
        ) : (
          <Grid container spacing={2}>
            {invoices.map((invoice) => (
              <Grid item xs={12} key={invoice.id || invoice.fdn || JSON.stringify(invoice)}>
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography fontWeight={600}>
                    {invoice.buyer_name || invoice.description || "Fiscal invoice"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Amount: {invoice.amount || "—"} · FDN:{" "}
                    {invoice.fdn || invoice.fiscal_document_number || "—"}
                  </Typography>
                  {(invoice.validation_url ||
                    (typeof invoice.qr_code === "string" &&
                      invoice.qr_code.startsWith("http")) ||
                    invoice.qr_code_url) && (
                    <Button
                      size="small"
                      sx={{ mt: 1, px: 0 }}
                      href={
                        invoice.validation_url ||
                        invoice.qr_code ||
                        invoice.qr_code_url
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View URA Receipt
                    </Button>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TopSnack
        open={openSnack.open}
        handleCloseSnack={handleCloseSnack}
        message={openSnack.message}
        severity={openSnack.severity}
      />
    </Box>
  );
};

export default EInvoicing;
